import React, { useState } from 'react';
import { User } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export default function AddStudent() {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    age: '',
    mobile: '',
    alternateMobile: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  const [photoPreview, setPhotoPreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      dateOfBirth: value
    }));

    if (value) {
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    console.log('Form Data:', formData);
    alert('Student information saved successfully!');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">Add New Student</h1>
      
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex gap-8">
          {/* Photo Upload Section */}
          <div className="flex flex-col items-center gap-4 pt-8">
            <div className="w-32 h-32 bg-[#4AACC5] rounded-full flex items-center justify-center overflow-hidden">
              {photoPreview ? (
                <img src={photoPreview} alt="Student" className="w-full h-full object-cover" />
              ) : (
                <User className="w-16 h-16 text-white" />
              )}
            </div>
            <label className="px-6 py-2 bg-white border-2 border-[#4AACC5] text-[#4AACC5] text-sm font-medium rounded cursor-pointer hover:bg-cyan-50 transition-colors">
              Upload Photo
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>

          {/* Vertical Divider */}
          <div className="w-px bg-gray-200"></div>

          {/* Form Section */}
          <div className="flex-1">
            {/* Student Information */}
            <div className="mb-8">
              <h2 className="text-base font-semibold text-gray-800 mb-4">Student Information</h2>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter First Name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Middle Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    placeholder="Enter Middle Name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter Last Name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Date Of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dateOfBirth"
                    placeholder="MM/DD/YYYY"
                    value={formData.dateOfBirth}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length >= 2) {
                        value = value.slice(0, 2) + '/' + value.slice(2);
                      }
                      if (value.length >= 5) {
                        value = value.slice(0, 5) + '/' + value.slice(5, 9);
                      }
                      setFormData(prev => ({ ...prev, dateOfBirth: value }));
                      
                      if (value.length === 10) {
                        const [month, day, year] = value.split('/');
                        const birthDate = new Date(year, month - 1, day);
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const monthDiff = today.getMonth() - birthDate.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                          age--;
                        }
                        setFormData(prev => ({ ...prev, age: age.toString() }));
                      }
                    }}
                    maxLength="10"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="age"
                    value={formData.age}
                    readOnly
                    placeholder="Auto calculated"
                    className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded text-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div>
              <h2 className="text-base font-semibold text-gray-800 mb-4">Contact Details</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Mobile <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    country={'us'}
                    value={formData.mobile}
                    onChange={(phone) => setFormData(prev => ({ ...prev, mobile: phone }))}
                    inputStyle={{
                      width: '100%',
                      height: '38px',
                      fontSize: '14px',
                      borderColor: '#d1d5db',
                      borderRadius: '4px'
                    }}
                    buttonStyle={{
                      borderColor: '#d1d5db',
                      borderRadius: '4px 0 0 4px'
                    }}
                    containerClass="phone-input-container"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Alternate Mobile
                  </label>
                  <PhoneInput
                    country={'us'}
                    value={formData.alternateMobile}
                    onChange={(phone) => setFormData(prev => ({ ...prev, alternateMobile: phone }))}
                    inputStyle={{
                      width: '100%',
                      height: '38px',
                      fontSize: '14px',
                      borderColor: '#d1d5db',
                      borderRadius: '4px'
                    }}
                    buttonStyle={{
                      borderColor: '#d1d5db',
                      borderRadius: '4px 0 0 4px'
                    }}
                    containerClass="phone-input-container"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter Address"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5] text-gray-500"
                  >
                    <option value="">Select City</option>
                    <option value="new-york">New York</option>
                    <option value="los-angeles">Los Angeles</option>
                    <option value="chicago">Chicago</option>
                    <option value="houston">Houston</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5] text-gray-500"
                  >
                    <option value="">Select State</option>
                    <option value="ny">New York</option>
                    <option value="ca">California</option>
                    <option value="il">Illinois</option>
                    <option value="tx">Texas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-2">
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="Enter Zip Code"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#4AACC5]"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs text-gray-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded"
                />
              </div>

              <button
                onClick={handleSubmit}
                className="w-full py-3 bg-[#4AACC5] text-white text-sm font-medium rounded hover:bg-cyan-500 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}